import { Box, Checkbox, Link, Tooltip, Typography } from "@mui/material"
import { useDraggable } from "@dnd-kit/core"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { ListItemBox } from "lib/@components/ListItemBox"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { If } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { isScheduleSelected } from "./board-selection"
import { formatHours } from "./format-hours"

/**
 * One schedule on the left pane: tick it (shift-click for a range), drag it onto a trade,
 * or click its title to give individual tasks a different trade. The caption shows how
 * the schedule is routed today by skill, e.g. "Mixed: Gas, Plumbing".
 */
export function UnassignedScheduleRow({ schedule, group }) {
    const { selection, readOnly, onToggleSchedule, onOpenSchedule } = useBoundContext()
    const selected = isScheduleSelected(selection, schedule)
    const cursor = readOnly ? "default" : "grab"
    const background = selected ? "action.selected" : "background.paper"
    // Only the pointer listeners are spread: dnd-kit's attributes would make the whole row a
    // tab stop announced as a draggable button, and there is no keyboard drag (see the board)
    const { listeners, setNodeRef, isDragging } = useDraggable({
        id: `schedule:${schedule.scheduleId}`,
        data: { group, schedule },
        disabled: readOnly,
    })

    return (
        <ListItemBox
            ref={setNodeRef}
            spacing={1}
            {...listeners}
            data-cy="unassigned-schedule-row"
            sx={{
                pl: 2,
                // Leaves room under the folder rows' expand chevron so the hours line up
                pr: 5.75,
                py: 0.5,
                borderTop: 1,
                borderColor: "divider",
                bgcolor: background,
                cursor,
                opacity: dragOpacity(isDragging),
            }}
        >
            <If value={!readOnly}>
                <Checkbox
                    size="small"
                    checked={selected}
                    onClick={handleTick}
                    inputProps={{ "aria-label": `Select ${schedule.code} ${schedule.title}` }}
                    data-cy="assignment-schedule-checkbox"
                />
            </If>
            <Typography variant="body2" sx={{ fontFamily: "monospace", flexShrink: 0 }}>
                {schedule.code}
            </Typography>
            <Link
                component="button"
                variant="body2"
                color="inherit"
                underline="hover"
                onClick={handleOpen}
                sx={{ flex: 1, minWidth: 0, textAlign: "left", display: "block" }}
                data-cy="assignment-schedule-title"
            >
                {/* A span, as the title sits in a button; the full title shows on hover when cut */}
                <TruncatedTypography variant="body2" component="span" sx={{ display: "block" }}>
                    {schedule.title}
                </TruncatedTypography>
            </Link>
            <Box sx={{ maxWidth: 220, minWidth: 0 }}>
                <TruncatedTypography variant="caption" color="text.secondary">
                    {schedule.routing}
                </TruncatedTypography>
            </Box>
            <If value={schedule.conditionalRules?.length > 0}>
                {/* Iconify cannot hold the ref Tooltip needs, so a focusable span carries it */}
                <Tooltip
                    title={`Conditional skill rules can send some tasks elsewhere: ${schedule.conditionalRules?.join(", ")}`}
                >
                    <Box
                        component="span"
                        tabIndex={0}
                        aria-label="Conditional skill rules apply"
                        sx={{ display: "inline-flex", color: "text.secondary", flexShrink: 0 }}
                        data-cy="schedule-routing-conditional"
                    >
                        <Iconify icon="mdi:call-split" width={16} />
                    </Box>
                </Tooltip>
            </If>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: "right" }}>
                {formatHours(schedule.annualHours)}
            </Typography>
        </ListItemBox>
    )

    // onClick (not onChange) so the shift key is available for range selection
    function handleTick(event) {
        event.stopPropagation()
        onToggleSchedule(group, schedule.scheduleId, event.shiftKey)
    }

    function handleOpen(event) {
        event.stopPropagation()
        onOpenSchedule(schedule.scheduleId)
    }
}

function dragOpacity(isDragging) {
    return isDragging ? 0.5 : 1
}
